import React from "react";

import {
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { Eye as Visibility } from "react-feather";
import { EyeOff as VisibilityOff } from "react-feather";

import { FormHeader } from "@hubcapp/components";
import Button from "@ds-developer/button";

import { SignInFormProps } from "./types";

const useStyles = makeStyles(theme => ({
  fields: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    "& > *": {
      flexGrow: 1,
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    marginTop: theme.spacing(1),
    minWidth: "100px",
    minHeight: "42px",
  },
  footerLinks: {
    marginTop: theme.spacing(2),
  },
  createLink: {
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      justifyContent: "flex-end",
    },
  },
  spinner: {
    color: "#FFF",
  },
}));

const SignInForm: React.FC<SignInFormProps> = ({
  errors,
  handleClickShowPassword,
  handleMouseDownPassword,
  handleSubmit,
  handleADIEvent,
  handlePasswordEvent,
  handleForgottenPasswordEvent,
  handleCreateAccountEvent,
  isValid,
  loading,
  primaryErrorMessage,
  register,
  showPassword = false,
  changePasswordPath,
  signUpPath,
  brand,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <form {...rest} onSubmit={handleSubmit}>
      <Typography variant="h1" component="h1" gutterBottom>
        First time on Hubcapp?
      </Typography>
      <Typography
        color="textPrimary"
        data-test="form-header-first-time"
        variant="body1"
      >
        You need to{" "}
        <Link
          component={RouterLink}
          variant="body1"
          data-test="create-account-link"
          to={signUpPath}
          onClick={handleCreateAccountEvent}
        >
          create
        </Link>{" "}
        a new Hubcapp account. This is separate to your old MyDrive account.
      </Typography>

      <Divider style={{ marginTop: "24px", marginBottom: "24px" }} />

      <FormHeader
        testId="sign-in-form-header"
        title="Sign in"
        subtitle={
          primaryErrorMessage
            ? primaryErrorMessage
            : "Sign in to Hubcapp, the instructor portal."
        }
        error={Boolean(primaryErrorMessage)}
        showBottomDivider={false}
      />
      <div className={classes.fields}>
        <TextField
          error={Boolean(errors?.adiNumber)}
          fullWidth
          helperText={errors?.adiNumber?.message}
          inputProps={{
            inputMode: "decimal",
            className: "data-hj-allow",
          }}
          inputRef={register}
          // TODO: Fix
          label={`Your ${
            brand !== undefined ? brand.toUpperCase() : ""
          } instructor ID`}
          name="adiNumber"
          type="text"
          variant="outlined"
          onClick={handleADIEvent}
        />
        <TextField
          error={Boolean(errors?.password)}
          fullWidth
          helperText={errors?.password?.message}
          inputRef={register}
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onClick={handlePasswordEvent}
        />
      </div>
      <Button
        className={classes.submitButton}
        disabled={!isValid}
        size="large"
        type="submit"
        variant="contained"
        color="primary"
        loading={loading}
      >
        Sign in
      </Button>
      <Grid container spacing={2} className={classes.footerLinks}>
        <Grid item xs={12} md={6}>
          <Link
            component={RouterLink}
            variant="body1"
            to={changePasswordPath}
            data-test="forgotten-password-link"
            onClick={handleForgottenPasswordEvent}
          >
            Forgotten your password?
          </Link>
        </Grid>

        <Grid item xs={12} md={6} className={classes.createLink}>
          <Link
            component={RouterLink}
            variant="body1"
            data-test="create-account-link"
            to={signUpPath}
            onClick={handleCreateAccountEvent}
          >
            Create an account
          </Link>
        </Grid>
      </Grid>
    </form>
  );
};

export default SignInForm;
